<template>
  <div class="auth">
    <div class="container">
      <div class="auth--box">
        <div class="auth--box--header">
          <h2 class="auth--title">{{ $t("Account Profile") }}</h2>
        </div>
        <form @submit.prevent="submit">
          <avatar />
          <v-text-field
            type="text"
            name="name"
            :label="$t('full_name')"
            class="mb-3"
            v-model="user.full_name"
            v-validate="'required|alpha_spaces|full_name|min_length:3'"
            :error="!!fullNameErrors"
            :error-messages="fullNameErrors"
            clearable
          ></v-text-field>
          <v-text-field
            type="text"
            name="email"
            :label="$t('email')"
            disabled
            class="mb-3"
            v-model="user.email"
          ></v-text-field>
          <vue-tel-input-vuetify
            v-model="user.phone_number"
            :label="$t('phone_number')"
            clearable
            class="mb-3"
            name="phone_number"
            mode="international"
            v-validate="'required|phone'"
            :error="!!phoneNumberErrors"
            :error-messages="phoneNumberErrors"
            v-on:input="changePhoneNumber"
          >
          </vue-tel-input-vuetify>
          <v-autocomplete
            v-model="user.address.country_id"
            id="country"
            name="country"
            :items="countries"
            item-text="name"
            item-value="id"
            :label="$t('country')"
            v-validate="isAddressRequired"
            clearable
            auto-select-first
            :error="!!countryErrors"
            :error-messages="countryErrors"
          ></v-autocomplete>
          <v-text-field
            type="text"
            name="street_address"
            :label="$t('street_address')"
            class="mb-3"
            v-model="user.address.street_address"
            :error="!!streetAddressErrors"
            :error-messages="streetAddressErrors"
            v-validate="`${isAddressRequired}|min_words:3|alpha_spaces`"
            clearable
          ></v-text-field>
          <v-text-field
            type="text"
            name="city"
            :label="$t('city')"
            class="mb-3"
            v-model="user.address.city"
            :error="!!cityErrors"
            :error-messages="cityErrors"
            v-validate="`${isAddressRequired}|alpha_spaces`"
            clearable
          ></v-text-field>
          <v-text-field
            type="number"
            name="zip_code"
            :label="$t('zip_code')"
            class="mb-3"
            v-model="user.address.zip_code"
            :error="!!zipCodeErrors"
            :error-messages="zipCodeErrors"
            v-validate="`${isAddressRequired}|numeric|min:5|max:9`"
            clearable
          ></v-text-field>
          <div class="d-flex justify-content-center">
            <v-btn class="auth-btn" color="success" type="submit">
              {{ $t("Save Info") }}
            </v-btn>
          </div>
        </form>
      </div>
    </div>

    <v-dialog v-model="confirmationDialog" persistent max-width="330">
      <v-card>
        <v-card-title class="text-h5">
          {{ $t("saveChanges?") }}
        </v-card-title>
        <v-card-text>
          {{ $t("saveProfileChanges") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click="confirmationDialog = false">
            {{ $t("Cancel") }}
          </v-btn>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            color="green darken-1"
            text
            @click="updateProfileData"
          >
            {{ $t("Save Info") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import GlobalMixin from "@/GlobalMixin.js";
import Avatar from "../components/Avatar.vue";
import { mapActions, mapGetters } from "vuex";
export default {
  mixins: [GlobalMixin],
  components: {
    Avatar,
  },
  data() {
    return {
      user: {
        full_name: "",
        email: "",
        phone_number: "",
        address: {
          country_id: "",
          street_address: "",
          city: "",
          zip_code: "",
        },
      },
      isLoading: false,
      confirmationDialog: false,
    };
  },
  mounted() {
    this.getProfileData();
  },
  methods: {
    ...mapActions(["UpdateUser"]),
    /**
     * @method getProfileData
     * @description Fetches the user's profile data
     * @returns {void}
     */
    getProfileData() {
      const id = this.StateUser.user.id;
      this.axios(
        `${process.env.VUE_APP_APIEndPoint}auth/profile_info/${id}`
      ).then(({ data }) => {
        if (!data.address) data.address = {};
        this.user = data;
        this.UpdateUser(data);
      });
    },

    /**
     * @method updateProfileData
     * @description Updates the user's profile data
     * @returns {void}
     */
    updateProfileData() {
      if (this.isLoading) return;

      this.isLoading = true;
      const body = {
        ...this.user,
        first_name: this.user.full_name.split(" ")[0],
        last_name: this.user.full_name
          .split(" ")
          .slice(1)
          .join(" "),
        phone_number: this.user.phone_number.replace(/\s/g, ""),
        address: {
          ...this.user.address,
          country: this.user.address.country_id,
        },
      };

      delete body.address.country_id;

      if (
        !body.address.country &&
        !body.address.zip_code &&
        !body.address.city &&
        !body.address.street_address
      ) {
        body.address = null;
        body.delete_empty_address = true;
      }

      this.axios
        .post(
          `${process.env.VUE_APP_APIEndPoint}auth/update_profile_info/`,
          body,
          this.headers
        )
        .then(() => {
          this.notify(this.$t("savedSucess"), "success");
          this.UpdateUser(this.user);
        })
        .catch((err) => this.displayError(err))
        .finally(() => {
          this.isLoading = false;
          this.confirmationDialog = false;
        });
    },

    /**
     * @method submit
     * @description Submits the form of the user's profile data
     * @returns {void}
     */
    async submit() {
      const isValid = await this.$validator.validate();
      if (!isValid) return this.notify(this.$t("validateFields"), "error");
      this.confirmationDialog = true;
    },

    changePhoneNumber(val, { number, valid, country }) {
      this.user.phone_number = number.e164;
    },
  },
  computed: {
    ...mapGetters(["StateUser"]),
    fullNameErrors() {
      return this.errors.first("name");
    },
    phoneNumberErrors() {
      return this.errors.first("phone_number");
    },
    countryErrors() {
      return this.errors.first("country");
    },
    streetAddressErrors() {
      return this.errors.first("street_address");
    },
    cityErrors() {
      return this.errors.first("city");
    },
    zipCodeErrors() {
      return this.errors.first("zip_code");
    },
    isAddressRequired() {
      if (!this.user.address) return "";
      const { country_id, zip_code, city, street_address } = this.user.address;
      return country_id || zip_code || city || street_address ? "required" : "";
    },
  },
};
</script>
